import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Briefcase, RefreshCw, Search, X, History, ShieldOff, PauseCircle, PlayCircle, Archive, XCircle, Clock, Trash2, CheckSquare, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { adminAPI } from '@/services/adminAPI';
import Pagination from '@/components/shared/Pagination';
import ScrollHint from '@/components/shared/ScrollHint';

const STATUS_META = {
  active:   { label: 'Active',   tone: 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400', icon: PlayCircle },
  paused:   { label: 'Paused',   tone: 'bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400', icon: PauseCircle },
  expired:  { label: 'Expired',  tone: 'bg-slate-100 dark:bg-zinc-900 text-slate-600 dark:text-zinc-400', icon: Clock },
  closed:   { label: 'Closed',   tone: 'bg-slate-100 dark:bg-zinc-900 text-slate-600 dark:text-zinc-400', icon: XCircle },
  archived: { label: 'Archived', tone: 'bg-slate-100 dark:bg-zinc-900 text-slate-500 dark:text-zinc-500', icon: Archive },
  revoked:  { label: 'Revoked',  tone: 'bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400', icon: ShieldOff },
};

const ADMIN_STATUSES = ['active', 'paused', 'closed', 'expired', 'archived'];

function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return '—'; }
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.active;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.tone}`} data-testid={`job-status-${status}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

export default function AdminJobs() {
  const [data, setData] = useState({ jobs: [], total: 0, page: 1, limit: 25, statuses: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [drawer, setDrawer] = useState(null);
  const [changing, setChanging] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState(null); // { kind, status? }
  const [bulkReason, setBulkReason] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const load = async (opts = {}) => {
    setLoading(true);
    try {
      const p = { ...filters, page: opts.page || page, limit: opts.limit || pageSize };
      const res = await adminAPI.listJobs(p);
      setData(res);
      setSelected(new Set()); // reset selection on any reload
    } catch { toast.error('Failed to load jobs'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load({ page: 1 }); setPage(1);}, [filters.status, pageSize]);
  useEffect(() => { load({ page });}, [page]);

  const openDrawer = async (id) => {
    try {
      const job = await adminAPI.getJob(id);
      setDrawer(job);
    } catch { toast.error('Could not load job'); }
  };

  const changeStatus = async (id, status) => {
    if (status === 'revoked') { toast.error('Use the org-revoke flow'); return; }
    const reason = window.prompt(`Reason for changing status to "${status}" (optional):`, '');
    if (reason === null) return;
    setChanging(true);
    try {
      await adminAPI.setJobStatus(id, status, reason || '');
      toast.success(`Status updated to ${status}`);
      await load();
      if (drawer?.id === id) {
        const updated = await adminAPI.getJob(id);
        setDrawer(updated);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update status');
    } finally { setChanging(false); }
  };

  const filteredJobs = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    if (!q) return data.jobs;
    return data.jobs.filter((j) =>
      (j.title || '').toLowerCase().includes(q) ||
      (j.organization_name || '').toLowerCase().includes(q)
    );
  }, [data.jobs, filters.search]);

  const allSelectedOnPage = filteredJobs.length > 0 && filteredJobs.every(j => selected.has(j.id));
  const toggleSelectAll = () => {
    if (allSelectedOnPage) {
      const next = new Set(selected);
      filteredJobs.forEach(j => next.delete(j.id));
      setSelected(next);
    } else {
      const next = new Set(selected);
      filteredJobs.forEach(j => next.add(j.id));
      setSelected(next);
    }
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const runBulk = async () => {
    if (!bulkAction || selected.size === 0) return;
    const ids = [...selected];
    const payload = {
      job_ids: ids,
      action: bulkAction.kind,
      status: bulkAction.status || '',
      reason: bulkReason || '',
    };
    setChanging(true);
    try {
      const res = await adminAPI.bulkJobAction(payload);
      const s = res.succeeded_count, f = res.failed_count;
      if (f === 0) toast.success(`${s} job${s === 1 ? '' : 's'} updated`);
      else toast.message(`${s} succeeded, ${f} failed`, { description: (res.failed || []).slice(0, 3).map(x => `${x.id.slice(-6)}: ${x.error}`).join(' · ') });
      setBulkAction(null); setBulkReason(''); setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk action failed');
    } finally { setChanging(false); }
  };

  return (
    <div data-testid="admin-jobs">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2">
            <Briefcase className="w-6 h-6" /> Jobs
          </h1>
          <p className="text-slate-500 dark:text-zinc-500 text-sm">
            Every job posting across the platform. Change status, review history, or take over from a recruiter.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load()} disabled={loading} data-testid="admin-jobs-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1 bg-slate-100 dark:bg-zinc-900 rounded-full p-1">
          {['', ...Object.keys(STATUS_META)].map((s) => (
            <button
              key={s || 'all'}
              onClick={() => setFilters((f) => ({ ...f, status: s }))}
              className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium transition ${filters.status === s ? 'bg-white dark:bg-zinc-800 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-zinc-500'}`}
              data-testid={`admin-jobs-tab-${s || 'all'}`}
            >
              {s ? STATUS_META[s].label : 'All'}
            </button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2 bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-full px-3 py-1.5">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <input
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            placeholder="Search title or org…"
            className="bg-transparent outline-none text-[13px] text-slate-900 dark:text-white placeholder:text-slate-400 w-56"
            data-testid="admin-jobs-search"
          />
        </form>
        <span className="text-[12px] text-slate-500 dark:text-zinc-500 ml-auto">Total: <span className="font-mono">{data.total}</span></span>
      </div>

      {/* ===== BULK ACTION BAR ===== */}
      {selected.size > 0 && (
        <div className="bg-slate-900 dark:bg-zinc-900 border border-slate-700 dark:border-zinc-700 text-white rounded-lg px-4 py-2.5 mb-3 flex flex-wrap items-center gap-3" data-testid="admin-jobs-bulk-bar">
          <span className="text-sm font-medium" data-testid="admin-jobs-bulk-count">{selected.size} selected</span>
          <span className="text-slate-400 text-xs">Change status →</span>
          {ADMIN_STATUSES.map(s => (
            <button
              key={s}
              onClick={() => setBulkAction({ kind: 'change_status', status: s })}
              className="text-xs px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-white"
              data-testid={`admin-jobs-bulk-status-${s}`}
            >{STATUS_META[s].label}</button>
          ))}
          <span className="text-slate-500 text-xs">|</span>
          <button
            onClick={() => setBulkAction({ kind: 'archive' })}
            className="text-xs px-2.5 py-1 rounded bg-amber-500/80 hover:bg-amber-500 text-black font-medium inline-flex items-center gap-1"
            data-testid="admin-jobs-bulk-archive"
          ><Archive className="w-3 h-3" /> Archive</button>
          <button
            onClick={() => setBulkAction({ kind: 'delete' })}
            className="text-xs px-2.5 py-1 rounded bg-red-600 hover:bg-red-500 text-white font-medium inline-flex items-center gap-1"
            data-testid="admin-jobs-bulk-delete"
          ><Trash2 className="w-3 h-3" /> Delete</button>
          <button
            onClick={() => setSelected(new Set())}
            className="ml-auto text-xs text-slate-400 hover:text-white"
            data-testid="admin-jobs-bulk-clear"
          >Clear selection</button>
        </div>
      )}

      <ScrollHint>
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg overflow-x-auto">
        {loading ? (
          <p className="p-8 text-center text-slate-400 text-sm">Loading…</p>
        ) : filteredJobs.length === 0 ? (
          <p className="p-8 text-center text-slate-500 dark:text-zinc-500 text-sm">No jobs match the filters.</p>
        ) : (
          <table className="w-full text-sm min-w-[820px]">
            <thead className="border-b border-slate-100 dark:border-zinc-800 text-slate-500 dark:text-zinc-500 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 w-10">
                  <button onClick={toggleSelectAll} className="text-slate-500 hover:text-slate-900 dark:hover:text-white" data-testid="admin-jobs-select-all" aria-label="Select all">
                    {allSelectedOnPage ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                  </button>
                </th>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Organization</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Updated</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map((j) => (
                <tr key={j.id} className={`border-b border-slate-100 dark:border-zinc-900 last:border-0 hover:bg-slate-50 dark:hover:bg-zinc-950 ${selected.has(j.id) ? 'bg-blue-50/40 dark:bg-blue-950/20' : ''}`} data-testid={`admin-jobs-row-${j.id}`}>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleOne(j.id)} className="text-slate-500 hover:text-slate-900 dark:hover:text-white" data-testid={`admin-jobs-select-${j.id}`}>
                      {selected.has(j.id) ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => openDrawer(j.id)} className="text-left font-medium text-slate-900 dark:text-white hover:text-[#6D5EF7] transition" data-testid={`admin-jobs-title-${j.id}`}>
                      {j.title || 'Untitled'}
                    </button>
                    <div className="text-[11.5px] text-slate-400 dark:text-zinc-500 font-mono truncate max-w-[420px]">#{(j.id || '').slice(-8)}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-zinc-300">
                    <div>{j.organization_name || '—'}</div>
                    {j.organization_domain_status === 'revoked' && (
                      <span className="inline-flex items-center gap-1 text-[10.5px] text-red-500 mt-0.5"><ShieldOff className="w-3 h-3" /> domain revoked</span>
                    )}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={j.status || 'active'} /></td>
                  <td className="px-4 py-3 text-slate-500 dark:text-zinc-500 text-[12.5px]">{fmt(j.updated_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex flex-wrap gap-1 justify-end">
                      {ADMIN_STATUSES.filter((s) => s !== j.status).map((s) => (
                        <button
                          key={s}
                          onClick={() => changeStatus(j.id, s)}
                          disabled={changing || j.status === 'revoked'}
                          className={`text-[11px] px-2 py-1 rounded font-medium transition ${j.status === 'revoked' ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-100 dark:hover:bg-zinc-900 text-slate-600 dark:text-zinc-400'}`}
                          title={j.status === 'revoked' ? 'Restore the organization to re-enable' : `Set to ${s}`}
                          data-testid={`admin-jobs-set-${s}-${j.id}`}
                        >
                          {STATUS_META[s].label}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </ScrollHint>

      <Pagination
        page={page}
        pageSize={pageSize}
        total={data.total || 0}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        testIdPrefix="admin-jobs-pg"
      />

      {/* Bulk action confirmation dialog */}
      {bulkAction && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setBulkAction(null)}>
          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-jobs-bulk-dialog">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {bulkAction.kind === 'delete' ? 'Delete jobs permanently?' :
               bulkAction.kind === 'archive' ? 'Archive jobs?' :
               `Change ${selected.size} job${selected.size === 1 ? '' : 's'} to ${STATUS_META[bulkAction.status]?.label || bulkAction.status}?`}
            </h3>
            <p className="text-slate-500 dark:text-zinc-400 text-sm mb-4">
              {bulkAction.kind === 'delete'
                ? `This will permanently delete ${selected.size} job${selected.size === 1 ? '' : 's'} and cascade-remove ALL associated matches, interviews and screenings. This cannot be undone.`
                : `This will apply the change to ${selected.size} selected job${selected.size === 1 ? '' : 's'}. A reason will be recorded in each job's status history.`}
            </p>
            <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-1">Reason (optional, stored in audit log)</label>
            <input
              value={bulkReason}
              onChange={(e) => setBulkReason(e.target.value)}
              placeholder="e.g. cleanup of test jobs"
              className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white mb-4"
              data-testid="admin-jobs-bulk-reason"
            />
            <div className="flex gap-3">
              <button onClick={() => setBulkAction(null)} className="flex-1 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm" data-testid="admin-jobs-bulk-cancel">Cancel</button>
              <button
                onClick={runBulk}
                disabled={changing}
                className={`flex-1 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 ${bulkAction.kind === 'delete' ? 'bg-red-600 hover:bg-red-500' : 'bg-slate-900 dark:bg-white dark:text-black hover:bg-slate-800 dark:hover:bg-slate-100'}`}
                data-testid="admin-jobs-bulk-confirm">
                {changing ? 'Working…' : (bulkAction.kind === 'delete' ? 'Delete permanently' : 'Apply')}
              </button>
            </div>
          </div>
        </div>
      )}

      {drawer && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setDrawer(null)}>
          <div className="flex-1 bg-black/40" />
          <aside className="w-full max-w-[520px] h-full overflow-y-auto bg-white dark:bg-[#0d0d10] border-l border-slate-200 dark:border-zinc-800 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="admin-jobs-drawer">
            <div className="sticky top-0 flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-zinc-800 bg-white/95 dark:bg-[#0d0d10]/95 backdrop-blur">
              <div>
                <p className="text-[11px] text-slate-400 uppercase tracking-wide">Job details</p>
                <h3 className="text-[15px] font-semibold text-slate-900 dark:text-white truncate max-w-[360px]">{drawer.title || 'Untitled'}</h3>
              </div>
              <button onClick={() => setDrawer(null)} className="grid place-items-center h-8 w-8 rounded hover:bg-slate-100 dark:hover:bg-zinc-900 text-slate-500" data-testid="admin-jobs-drawer-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <StatusBadge status={drawer.status || 'active'} />
                <span className="text-[12px] text-slate-500 dark:text-zinc-500">Updated {fmt(drawer.updated_at)}</span>
              </div>
              <Info label="Organization">{drawer.organization_name || '—'}</Info>
              <Info label="Recruiter">{drawer.recruiter_email || drawer.recruiter_name || '—'}</Info>
              {drawer.status_changed_reason && (
                <Info label="Last status change reason">{drawer.status_changed_reason}</Info>
              )}

              <div className="pt-2">
                <p className="text-[11px] text-slate-500 uppercase tracking-wide mb-2">Change status</p>
                <div className="flex flex-wrap gap-1.5">
                  {ADMIN_STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => changeStatus(drawer.id, s)}
                      disabled={changing || s === drawer.status || drawer.status === 'revoked'}
                      className={`text-[12px] px-3 py-1.5 rounded-full font-medium transition ${s === drawer.status ? 'bg-[#6D5EF7]/15 text-[#6D5EF7]' : 'bg-slate-100 dark:bg-zinc-900 text-slate-700 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-800'} ${(changing || s === drawer.status || drawer.status === 'revoked') ? 'opacity-70 cursor-not-allowed' : ''}`}
                      data-testid={`admin-jobs-drawer-set-${s}`}
                    >
                      {STATUS_META[s].label}
                    </button>
                  ))}
                </div>
                {drawer.status === 'revoked' && (
                  <p className="mt-2 text-[11.5px] text-red-500 flex items-center gap-1"><ShieldOff className="w-3 h-3" /> Restore the organization first to re-enable this job.</p>
                )}
              </div>

              {Array.isArray(drawer.status_history) && drawer.status_history.length > 0 && (
                <div className="pt-2">
                  <p className="text-[11px] text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5"><History className="w-3 h-3" /> History</p>
                  <ul className="space-y-2">
                    {drawer.status_history.slice().reverse().map((h, idx) => (
                      <li key={idx} className="rounded-md border border-slate-100 dark:border-zinc-800 p-2.5">
                        <div className="text-[12px] text-slate-700 dark:text-zinc-300">
                          <b>{h.from_status}</b> → <b>{h.to_status}</b>
                        </div>
                        {h.reason && <div className="text-[11.5px] text-slate-500 mt-0.5">{h.reason}</div>}
                        <div className="text-[10.5px] text-slate-400 mt-1 font-mono">{fmt(h.at)}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <p className="text-[10.5px] text-slate-400 uppercase tracking-wide">{label}</p>
      <p className="text-[13px] text-slate-800 dark:text-zinc-200 mt-0.5 break-words">{children}</p>
    </div>
  );
}
